import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { TrendingUp, Info } from "lucide-react";
import { Layout } from "../components/Layout";
import { AnimatedNumber } from "../components/ScoreRing";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { Slider } from "../components/ui/slider";

const LEVERS = [
  { key: "posts", label: "Publications / semaine", min: 0, max: 14, def: 3, w: 1.4 },
  { key: "promos", label: "Promotions / mois", min: 0, max: 8, def: 2, w: 2.1 },
  { key: "events", label: "Animations / mois", min: 0, max: 6, def: 1, w: 3.0 },
  { key: "loyalty", label: "Campagnes fidélité / mois", min: 0, max: 6, def: 1, w: 2.6 },
  { key: "budget", label: "Budget marketing (€/mois)", min: 0, max: 1000, def: 200, w: 0.02, step: 50 },
];

export default function GrowthSimulator() {
  const { data } = useApp();
  const { t } = useLang();
  const base = data?.scores?.global || 60;
  const [vals, setVals] = useState(Object.fromEntries(LEVERS.map((l) => [l.key, l.def])));

  const projection = useMemo(() => {
    const boost = LEVERS.reduce((acc, l) => acc + (vals[l.key] - l.def) * l.w, 0);
    const scoreGain = Math.max(-15, Math.min(22, Math.round(boost * 0.3)));
    const traffic = Math.max(-20, Math.min(65, Math.round(boost * 0.9)));
    const revenue = Math.max(-15, Math.min(55, Math.round(boost * 0.7)));
    const loyalty = Math.max(-10, Math.min(45, Math.round(boost * 0.55)));
    return { scoreGain, traffic, revenue, loyalty };
  }, [vals]);

  const chartData = [
    { name: "Score", value: projection.scoreGain, color: "#6366F1" },
    { name: "Trafic", value: projection.traffic, color: "#00E5FF" },
    { name: "CA", value: projection.revenue, color: "#10B981" },
    { name: "Fidélité", value: projection.loyalty, color: "#F59E0B" },
  ];

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center text-neon"><TrendingUp size={22} /></div>
        <div><h1 className="font-display font-black text-2xl lg:text-3xl">{t("title_growth")}</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Que se passerait-il si… ?</p></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="lb-card p-6 space-y-6" data-testid="sim-levers">
          {LEVERS.map((l) => (
            <div key={l.key}>
              <div className="flex justify-between text-sm mb-3"><span className="text-[hsl(var(--muted-foreground))]">{l.label}</span><span className="font-semibold text-neon">{vals[l.key]}{l.key === "budget" ? "€" : ""}</span></div>
              <Slider data-testid={`sim-${l.key}`} value={[vals[l.key]]} min={l.min} max={l.max} step={l.step || 1} onValueChange={([v]) => setVals((p) => ({ ...p, [l.key]: v }))} />
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {chartData.map((c) => (
              <motion.div key={c.name} className="lb-card p-5" data-testid={`sim-metric-${c.name}`}>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{c.name}</p>
                <p className="font-display font-black text-2xl" style={{ color: c.value >= 0 ? c.color : "#EF4444" }}>
                  {c.value >= 0 ? "+" : ""}<AnimatedNumber value={c.value} />%
                </p>
              </motion.div>
            ))}
          </div>
          <div className="lb-card p-5" data-testid="sim-chart">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Projection d'impact (%)</p>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "#121218", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>{chartData.map((c, i) => <Cell key={i} fill={c.value >= 0 ? c.color : "#EF4444"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))] p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid="sim-disclaimer">
        <Info size={14} className="mt-0.5 shrink-0" />
        Projection indicative basée sur les données disponibles. Aucun résultat commercial n'est garanti.
      </div>
    </Layout>
  );
}
